import * as React from 'react';
import type { GameOutcome } from '@chess/api-model';
import { TextSkeleton } from '@chess/api-insights/charts';
import { opponentLabel, position, shortDate } from './label.ts';
import './game-bar.css';

// The dashboard screens' game bar, drawn from props only: in the middle, the
// selected game ("Oct 2 · Won · vs bob 1512": date, result, opponent and
// their rating) between « (the first, oldest game of the period), ‹ (the
// older), › (the newer) and » (the latest); at
// the right its place in the period ("38 of 41"). The label has a fixed
// width, so the arrows stay put while stepping through games. (A strip of result ticks
// was removed on 2026-10-06 as confusing; the Review link on 2026-10-07.) It heads the
// screen's per-game sections and sticks to
// the top of the viewport once the 30-day band above it has scrolled away,
// with a shadow only while stuck. Keyboard: ← and → step to the older and
// newer game, Home and End jump to the first and latest, wherever the focus
// is in the bar.

export interface GameBarItem {
  id: string;
  /** Epoch milliseconds. */
  playedAt: number;
  opponent?: string;
  /** The opponent's rating at the time of the game. */
  opponentRating?: number;
  outcome: GameOutcome;
}

export type GameBarProps =
  | { loading: true; stuck?: boolean }
  | {
      loading?: false;
      /** The period's games, newest first (the store's order). */
      games: readonly GameBarItem[];
      /** The selected game's index in `games`. */
      index: number;
      /** Steps to the newer (-1) or the older (+1) game. */
      onStep(delta: number): void;
      /** Selects the oldest (`first`) or the latest (`last`) game. */
      onJump(edge: 'first' | 'last'): void;
      /** Forces the stuck look (stories); measured on the page otherwise. */
      stuck?: boolean;
    };

const OUTCOME_WORD = { win: 'Won', draw: 'Drawn', loss: 'Lost' } as const;

// Phosphor CaretLeft and CaretRight, regular weight.
const CARET_LEFT = 'M165.66 202.34a8 8 0 0 1-11.32 11.32l-80-80a8 8 0 0 1 0-11.32l80-80a8 8 0 0 1 11.32 11.32L91.31 128Z';
const CARET_RIGHT = 'M181.66 133.66l-80 80a8 8 0 0 1-11.32-11.32L164.69 128 90.34 53.66a8 8 0 0 1 11.32-11.32l80 80a8 8 0 0 1 0 11.32Z';
// Phosphor CaretDoubleLeft and CaretDoubleRight, regular weight.
const CARET_FIRST = 'M205.66 202.34a8 8 0 0 1-11.32 11.32l-80-80a8 8 0 0 1 0-11.32l80-80a8 8 0 0 1 11.32 11.32L131.31 128ZM51.31 128l74.35-74.34a8 8 0 0 0-11.32-11.32l-80 80a8 8 0 0 0 0 11.32l80 80a8 8 0 0 0 11.32-11.32Z';
const CARET_LAST = 'M141.66 133.66l-80 80a8 8 0 0 1-11.32-11.32L124.69 128 50.34 53.66a8 8 0 0 1 11.32-11.32l80 80a8 8 0 0 1 0 11.32Zm80-11.32-80-80a8 8 0 0 0-11.32 11.32L204.69 128l-74.35 74.34a8 8 0 0 0 11.32 11.32l80-80a8 8 0 0 0 0-11.32Z';

const Caret: React.FC<{ d: string }> = ({ d }) => (
  <svg className="game-bar-caret" viewBox="0 0 256 256" aria-hidden="true">
    <path d={d} />
  </svg>
);

/** Whether the bar is stuck: a sentinel just above it has scrolled past the bar's sticky top. */
function useStuck(forced: boolean | undefined): [React.RefObject<HTMLDivElement>, React.RefObject<HTMLElement>, boolean] {
  const sentinel = React.useRef<HTMLDivElement>(null);
  const bar = React.useRef<HTMLElement>(null);
  const [stuck, setStuck] = React.useState(false);
  React.useEffect(() => {
    if (forced !== undefined || !sentinel.current || !bar.current || typeof IntersectionObserver === 'undefined') return;
    const top = parseFloat(getComputedStyle(bar.current).top) || 0;
    const observer = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting && entry.boundingClientRect.top < top),
      { rootMargin: `${-Math.ceil(top) - 1}px 0px 0px 0px`, threshold: 0 },
    );
    observer.observe(sentinel.current);
    return () => observer.disconnect();
  }, [forced]);
  return [sentinel, bar, forced ?? stuck];
}

export const GameBar: React.FC<GameBarProps> = (props) => {
  const [sentinel, bar, stuck] = useStuck(props.stuck);
  const className = `game-bar viz${stuck ? ' game-bar-stuck' : ''}`;
  if (props.loading) {
    return (
      <>
        <div ref={sentinel} className="game-bar-sentinel" aria-hidden="true" />
        <nav ref={bar} className={className} aria-label="Game" aria-busy="true">
          <span className="game-bar-nav">
            <span className="game-bar-step" aria-hidden="true"><Caret d={CARET_FIRST} /></span>
            <span className="game-bar-step" aria-hidden="true"><Caret d={CARET_LEFT} /></span>
            <span className="game-bar-game"><TextSkeleton sample="Oct 2 · Won · vs opponent 1500" /></span>
            <span className="game-bar-step" aria-hidden="true"><Caret d={CARET_RIGHT} /></span>
            <span className="game-bar-step" aria-hidden="true"><Caret d={CARET_LAST} /></span>
          </span>
          <span className="game-bar-position"><TextSkeleton sample="38 of 41" /></span>
        </nav>
      </>
    );
  }
  const { games, index, onStep, onJump } = props;
  const game = games[index];
  if (!game) return null;
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.altKey || e.metaKey || e.ctrlKey) return;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      onStep(e.key === 'ArrowLeft' ? 1 : -1);
    }
    if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      onJump(e.key === 'Home' ? 'first' : 'last');
    }
  };
  const opponent = opponentLabel(game);
  return (
    <>
      <div ref={sentinel} className="game-bar-sentinel" aria-hidden="true" />
      <nav ref={bar} className={className} aria-label="Game" onKeyDown={onKeyDown}>
        <span className="game-bar-nav">
          <button type="button" className="game-bar-step" aria-label="First game" disabled={index >= games.length - 1} onClick={() => onJump('first')}>
            <Caret d={CARET_FIRST} />
          </button>
          <button type="button" className="game-bar-step" aria-label="Older game" disabled={index >= games.length - 1} onClick={() => onStep(1)}>
            <Caret d={CARET_LEFT} />
          </button>
          <p className="game-bar-game" aria-live="polite" title={[shortDate(game.playedAt), OUTCOME_WORD[game.outcome], opponent].filter(Boolean).join(' · ')}>
            {shortDate(game.playedAt)} · <span className={`game-bar-outcome game-bar-outcome-${game.outcome}`}>{OUTCOME_WORD[game.outcome]}</span>
            {opponent && <> · {opponent}</>}
          </p>
          <button type="button" className="game-bar-step" aria-label="Newer game" disabled={index <= 0} onClick={() => onStep(-1)}>
            <Caret d={CARET_RIGHT} />
          </button>
          <button type="button" className="game-bar-step" aria-label="Latest game" disabled={index <= 0} onClick={() => onJump('last')}>
            <Caret d={CARET_LAST} />
          </button>
        </span>
        <span className="game-bar-position">{position(games.length, index)}</span>
      </nav>
    </>
  );
};
